/**
 * Login Page — Premium Glassmorphism Design
 *
 * Security Design:
 * - Passwords are sent over HTTPS to our backend
 * - Backend hashes passwords with bcrypt and stores only the hash
 * - On successful login/register, backend returns a token + user data
 * - Token is stored in localStorage for subsequent API calls
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  loginUser,
  registerUser,
  verifyLoginOTP,
  AUTH_TOKEN_STORAGE_KEY,
  normalizeSessionToken,
} from '../services/api';

function requireSessionToken(result, label) {
  const tok = normalizeSessionToken(result?.token);
  if (!tok) {
    throw new Error(
      `${label}: no token in response. Check backend JSON (expected { token, user }).`
    );
  }
  return tok;
}

function LoginPage({ onLogin, sessionExpired }) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // ── Login OTP step-up state ──
  const [otpStep, setOtpStep] = useState(false);
  const [loginToken, setLoginToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const otpInputRef = useRef(null);

  // Auto-focus OTP input when step changes
  useEffect(() => {
    if (otpStep && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [otpStep]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignup) {
        const result = await registerUser(email, password, displayName);
        const tok = requireSessionToken(result, 'Register');
        localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, tok);
        onLogin(result.user, tok);
      } else {
        const result = await loginUser(email, password);
        if (result.step === 'otp_required') {
          setLoginToken(result.login_token);
          setOtpMessage(
            result.message || 'A verification code was sent to your email. Check your inbox and spam folder.'
          );
          setOtpStep(true);
          setLoading(false);
          return;
        }
        const tok = requireSessionToken(result, 'Login');
        localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, tok);
        onLogin(result.user, tok);
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await verifyLoginOTP(loginToken, otpCode);
      const tok = requireSessionToken(result, 'Verify login');
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, tok);
      onLogin(result.user, tok);
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setOtpStep(false);
    setLoginToken('');
    setOtpCode('');
    setOtpMessage('');
    setError('');
  };

  return (
    <div style={{ ...styles.page, flexDirection: isMobile ? 'column' : 'row', overflowX: 'hidden' }}>
      {/* Animated background orbs */}
      <div style={styles.bgOrbs}>
        <div style={styles.orb1} />
        <div style={styles.orb2} />
        <div style={styles.orb3} />
      </div>

      {/* Left branding panel */}
      <div style={{
        ...styles.brandPanel,
        flex: isMobile ? '0 0 auto' : styles.brandPanel.flex,
        width: isMobile ? '100%' : undefined,
        minHeight: isMobile ? 'auto' : undefined,
        padding: isMobile ? '34px 20px 26px' : styles.brandPanel.padding,
      }}>
        <div style={styles.brandPanelOverlay} />
        <div style={{ ...styles.brandContent, maxWidth: isMobile ? '100%' : styles.brandContent.maxWidth }}>
          {/* Animated lock icon */}
          <div style={styles.brandIconOuter}>
            <div style={styles.brandIcon}>
              <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
          </div>
          <h1 style={{ ...styles.brandTitle, fontSize: isMobile ? 40 : styles.brandTitle.fontSize }}>JOSMY</h1>
          <p style={{ ...styles.brandSubtitle, fontSize: isMobile ? 14 : styles.brandSubtitle.fontSize }}>Cloud Data Encryption as a Service</p>
          <div style={styles.brandDivider} />
          <div style={styles.featureList}>
            <FeatureItem text="AES-256-GCM client-side encryption" delay={0} />
            <FeatureItem text="Zero-knowledge architecture" delay={1} />
            <FeatureItem text="Two-step OTP verification" delay={2} />
            <FeatureItem text="Your keys never leave your device" delay={3} />
          </div>

          {/* Security stats */}
          <div style={{ ...styles.statsRow, marginTop: isMobile ? 20 : styles.statsRow.marginTop, padding: isMobile ? '14px 0 4px' : styles.statsRow.padding }}>
            <div style={styles.stat}>
              <div style={styles.statValue}>256-bit</div>
              <div style={styles.statLabel}>Encryption</div>
            </div>
            <div style={styles.statDivider} />
            <div style={styles.stat}>
              <div style={styles.statValue}>100K</div>
              <div style={styles.statLabel}>PBKDF2 Rounds</div>
            </div>
            <div style={styles.statDivider} />
            <div style={styles.stat}>
              <div style={styles.statValue}>Zero</div>
              <div style={styles.statLabel}>Server Knowledge</div>
            </div>
          </div>
        </div>

        {/* Decorative grid pattern */}
        <div style={styles.gridPattern} />
      </div>

      {/* Right login panel */}
      <div style={{ ...styles.formPanel, padding: isMobile ? '24px 16px 30px' : styles.formPanel.padding }}>
        <div style={{ ...styles.formCard, maxWidth: isMobile ? 520 : styles.formCard.maxWidth }}>
          {sessionExpired && (
            <div style={styles.expiredBanner}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              Session expired due to inactivity. Please sign in again.
            </div>
          )}

          {/* ── OTP verification step ── */}
          {otpStep ? (
            <div style={{ animation: 'fadeInUp 0.4s ease-out' }}>
              <div style={styles.formIconWrap}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <h2 style={styles.formTitle}>Verify Your Identity</h2>
              <p style={styles.formSubtitle}>{otpMessage}</p>

              {error && <div style={styles.error}>{error}</div>}

              <form onSubmit={handleVerifyOTP} style={styles.form}>
                <div style={styles.field}>
                  <label style={styles.label}>Verification Code</label>
                  <input
                    ref={otpInputRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    style={{ ...styles.input, ...styles.otpInput }}
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                </div>

                <button type="submit" style={styles.submitBtn} className="hover-action-btn" disabled={loading || otpCode.length !== 6}>
                  {loading ? (
                    <span style={styles.btnContent}>
                      <span style={styles.spinner} />
                      Verifying...
                    </span>
                  ) : 'Verify & Sign In'}
                </button>
              </form>

              <p style={styles.switchText}>
                <button type="button" className="saas-back-btn" onClick={handleBackToLogin} style={{ marginTop: 8 }}>
                  <span className="saas-back-btn__icon" aria-hidden>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="19" y1="12" x2="5" y2="12" />
                      <polyline points="12 19 5 12 12 5" />
                    </svg>
                  </span>
                  Back to login
                </button>
              </p>

              <div style={styles.securityNote}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <span>
                  A 6-digit code applies to <strong>{email}</strong> (about 5 minutes). Use the code from your email.
                </span>
              </div>
            </div>
          ) : (
            <div style={{ animation: 'fadeInUp 0.4s ease-out' }}>
              {/* ── Normal login / signup form ── */}
              <div style={styles.formIconWrap}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <h2 style={styles.formTitle}>{isSignup ? 'Create Account' : 'Welcome Back'}</h2>
              <p style={styles.formSubtitle}>
                {isSignup ? 'Set up your secure encryption account' : 'Sign in to your secure vault'}
              </p>

              {error && <div style={styles.error}>{error}</div>}

              <form onSubmit={handleSubmit} style={styles.form}>
                {isSignup && (
                  <div style={styles.field}>
                    <label style={styles.label}>Full Name</label>
                    <div style={styles.inputWrap}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={styles.inputIcon}>
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        style={{ ...styles.input, paddingLeft: 48 }}
                        placeholder="Your full name"
                        required
                      />
                    </div>
                  </div>
                )}

                <div style={styles.field}>
                  <label style={styles.label}>Email Address</label>
                  <div style={styles.inputWrap}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={styles.inputIcon}>
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={{ ...styles.input, paddingLeft: 48 }}
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Password</label>
                  <div className="password-field">
                    <div style={styles.inputWrap}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={styles.inputIcon}>
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{ ...styles.input, paddingLeft: 48 }}
                        placeholder="Enter your password"
                        required
                      />
                    </div>
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <button type="submit" style={styles.submitBtn} className="hover-action-btn" disabled={loading}>
                  {loading ? (
                    <span style={styles.btnContent}>
                      <span style={styles.spinner} />
                      {isSignup ? 'Creating your account…' : 'Signing in…'}
                    </span>
                  ) : (isSignup ? 'Create Account' : 'Sign In')}
                </button>
              </form>

              {loading && !isSignup && (
                <p style={styles.loadingHint} aria-live="polite">
                  Verifying your password and sending a login code by email usually takes a few seconds (PBKDF2 runs later, when you encrypt files in the browser).
                </p>
              )}

              <p style={styles.switchText}>
                {isSignup ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button
                  type="button"
                  style={styles.switchBtn}
                  onClick={() => { setIsSignup(!isSignup); setError(''); }}
                >
                  {isSignup ? 'Sign In' : 'Sign Up'}
                </button>
              </p>

              <div style={styles.securityNote}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <span>Your files are encrypted locally before upload. We never see your plaintext data or encryption keys.</span>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function FeatureItem({ text, delay }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18,
      animation: `fadeInUp 0.5s ease-out ${0.3 + delay * 0.1}s both`,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 10,
        background: 'rgba(52,211,153,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        border: '1px solid rgba(52,211,153,0.1)',
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#34D399" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      </div>
      <span style={{ fontSize: 16, color: 'rgba(255,255,255,0.85)', fontWeight: 500, lineHeight: 1.5 }}>{text}</span>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
  },
  bgOrbs: {
    position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
    overflow: 'hidden',
  },
  orb1: {
    position: 'absolute', top: '10%', right: '20%',
    width: 450, height: 450, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 70%)',
    filter: 'blur(80px)',
    animation: 'float 8s ease-in-out infinite',
  },
  orb2: {
    position: 'absolute', bottom: '20%', left: '60%',
    width: 350, height: 350, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)',
    filter: 'blur(80px)',
    animation: 'float 10s ease-in-out infinite 2s',
  },
  orb3: {
    position: 'absolute', top: '50%', left: '30%',
    width: 280, height: 280, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(6,182,212,0.06) 0%, transparent 70%)',
    filter: 'blur(80px)',
    animation: 'float 12s ease-in-out infinite 4s',
  },
  brandPanel: {
    flex: '0 0 520px',
    background: 'linear-gradient(160deg, #06091A 0%, #0E1230 40%, #10153A 70%, #0E1230 100%)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '56px 52px',
    position: 'relative',
    overflow: 'hidden',
    zIndex: 1,
  },
  brandPanelOverlay: {
    position: 'absolute', inset: 0,
    background: 'radial-gradient(ellipse at 30% 20%, rgba(99,102,241,0.14) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(139,92,246,0.1) 0%, transparent 60%)',
    pointerEvents: 'none',
  },
  gridPattern: {
    position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.03,
    backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
    backgroundSize: '40px 40px',
  },
  brandContent: {
    maxWidth: 400,
    position: 'relative',
    zIndex: 2,
  },
  brandIconOuter: {
    marginBottom: 32,
    animation: 'fadeInUp 0.6s ease-out',
  },
  brandIcon: {
    width: 96,
    height: 96,
    borderRadius: 28,
    background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.15))',
    border: '1px solid rgba(129,140,248,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#818CF8',
    boxShadow: '0 12px 40px rgba(99,102,241,0.2), 0 0 0 1px rgba(129,140,248,0.1) inset',
    animation: 'float 6s ease-in-out infinite',
  },
  brandTitle: {
    fontSize: 44,
    fontWeight: 800,
    letterSpacing: '1px',
    margin: '0 0 10px 0',
    background: 'linear-gradient(135deg, #fff 0%, #A78BFA 50%, #818CF8 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    animation: 'fadeInUp 0.6s ease-out 0.1s both',
  },
  brandSubtitle: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.4)',
    margin: 0,
    fontWeight: 500,
    letterSpacing: '0.3px',
    animation: 'fadeInUp 0.6s ease-out 0.2s both',
  },
  brandDivider: {
    width: 56,
    height: 4,
    background: 'linear-gradient(90deg, #6366F1, #8B5CF6)',
    margin: '32px 0',
    borderRadius: 2,
    animation: 'fadeInUp 0.6s ease-out 0.25s both',
  },
  featureList: {
    marginTop: 0,
  },
  statsRow: {
    display: 'flex', alignItems: 'center', gap: 0,
    marginTop: 32, padding: '20px 0',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    animation: 'fadeInUp 0.6s ease-out 0.7s both',
  },
  stat: { flex: 1, textAlign: 'center' },
  statValue: { fontSize: 20, fontWeight: 700, color: '#A78BFA', letterSpacing: '0.5px', lineHeight: 1.2 },
  statLabel: { fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 6, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' },
  statDivider: { width: 1, height: 28, background: 'rgba(255,255,255,0.08)' },
  formPanel: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 32px',
    backgroundColor: 'var(--bg-body)',
    position: 'relative',
    zIndex: 1,
  },
  formCard: {
    width: '100%',
    maxWidth: 480,
  },
  formIconWrap: {
    width: 64, height: 64, borderRadius: 20,
    background: 'var(--blue-muted)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: 24,
    boxShadow: '0 4px 20px rgba(99,102,241,0.12)',
    border: '1px solid var(--border-subtle)',
  },
  expiredBanner: {
    backgroundColor: 'var(--yellow-light)',
    color: 'var(--yellow)',
    padding: '12px 16px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 20,
    border: '1px solid rgba(202,138,4,0.2)',
    display: 'flex', alignItems: 'center', gap: 10,
  },
  formTitle: {
    fontSize: 32,
    fontWeight: 800,
    color: 'var(--text-primary)',
    margin: '0 0 8px 0',
    letterSpacing: '-0.5px',
    lineHeight: 1.2,
  },
  formSubtitle: {
    fontSize: 16,
    color: 'var(--text-muted)',
    margin: '0 0 32px 0',
    lineHeight: 1.5,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 22,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--text-body)',
    letterSpacing: '0.2px',
  },
  inputWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute', left: 16, zIndex: 1,
    pointerEvents: 'none',
  },
  input: {
    padding: '16px 18px',
    minHeight: 52,
    border: '1.5px solid var(--border)',
    borderRadius: 14,
    fontSize: 16,
    outline: 'none',
    transition: 'all 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
    backgroundColor: 'var(--bg-card)',
    width: '100%',
    boxSizing: 'border-box',
    color: 'var(--text-primary)',
    fontFamily: 'inherit',
    boxShadow: '0 1px 2px rgba(99,102,241,0.04)',
  },
  otpInput: {
    textAlign: 'center', fontSize: 28, letterSpacing: 12, fontWeight: 700,
    padding: '18px', height: 64,
  },
  submitBtn: {
    padding: '16px 20px',
    background: 'linear-gradient(135deg, #6366F1 0%, #7C3AED 50%, #8B5CF6 100%)',
    backgroundSize: '200% 200%',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: 4,
    transition: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
    boxShadow: '0 4px 20px rgba(99,102,241,0.35)',
    letterSpacing: '0.3px',
    position: 'relative',
    overflow: 'hidden',
  },
  btnContent: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  },
  spinner: {
    width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)',
    borderTop: '2px solid #fff', borderRadius: '50%',
    animation: 'spin 0.7s linear infinite', display: 'inline-block',
  },
  loadingHint: {
    marginTop: 14,
    marginBottom: 0,
    fontSize: 13,
    lineHeight: 1.45,
    color: 'var(--text-muted)',
    textAlign: 'center',
  },
  switchText: {
    fontSize: 15,
    color: 'var(--text-muted)',
    textAlign: 'center',
    marginTop: 28,
  },
  switchBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--blue)',
    fontWeight: 700,
    cursor: 'pointer',
    textDecoration: 'none',
    padding: 0,
    fontSize: 14,
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'color 0.2s',
  },
  error: {
    backgroundColor: 'var(--red-light)',
    color: 'var(--red)',
    padding: '12px 16px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    border: '1px solid rgba(220,38,38,0.15)',
    marginBottom: 4,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  securityNote: {
    marginTop: 32,
    padding: '16px 20px',
    background: 'linear-gradient(135deg, rgba(16,185,129,0.06), rgba(6,182,212,0.04))',
    borderRadius: 'var(--radius)',
    fontSize: 14,
    color: 'var(--green)',
    lineHeight: 1.5,
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    border: '1px solid rgba(16,185,129,0.12)',
    boxShadow: '0 2px 8px rgba(16,185,129,0.06)',
  },
};

export default LoginPage;
